import { ISystemSetupCombinationValue } from '@/api/generator';
import { truthy } from '@/utils/helper';
import { isEqual, isNumber, unzip, zip } from 'lodash';
import { computed, Ref } from 'vue';

export type ParameterCombinationTitleValues =
  | {
      target: ISystemSetupCombinationValue[];
      support: ISystemSetupCombinationValue[];
    }
  | undefined;

type ParamCombination = {
  targetSystemValues?: Array<ISystemSetupCombinationValue> | null;
  supportSystemValues?: Array<ISystemSetupCombinationValue> | null;
};

function getUniqueParams(
  combinations: ParamCombination[] | undefined,
  system: 'target' | 'support',
) {
  const params = combinations
    ? zip(
        ...combinations.map((combination) => [
          ...(system === 'target' ? combination?.targetSystemValues || [] : []),
          ...(system === 'support' ? combination?.supportSystemValues || [] : []),
        ]),
      )
    : [];

  const variableParams: ISystemSetupCombinationValue[][] = params.map((param) => {
    const isVariable = !!param.find(
      (value) => !isEqual({ ...param[0], id: undefined }, { ...value, id: undefined }),
    );
    return isVariable ? param : new Array(param.length);
  });

  return unzip(variableParams);
}

function getUniqueParamIndices(
  combinations: ParamCombination[] | undefined,
  system: 'target' | 'support',
) {
  return (
    getUniqueParams(combinations, system)[0]
      ?.map((p, index) => (p === undefined ? undefined : index))
      .filter(isNumber) ?? []
  );
}

export function useTaskStepTitle(combinations: Ref<ParamCombination[] | undefined>) {
  const uniqueParamIndices = computed(() => ({
    target: getUniqueParamIndices(combinations.value, 'target'),
    support: getUniqueParamIndices(combinations.value, 'support'),
  }));

  function getTitle(combination?: ParamCombination): ParameterCombinationTitleValues {
    try {
      return {
        target: uniqueParamIndices.value.target
          .map((i) => combination?.targetSystemValues?.[i])
          .filter(truthy),

        support: uniqueParamIndices.value.support
          .map((i) => combination?.supportSystemValues?.[i])
          .filter(truthy),
      };
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error(e.message, e);
      }
    }
  }

  return {
    getTitle,
  };
}
